import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import testContextService from '../../services/TestContextService';
import { TestContextState } from './types';

const getTestContext = createAsyncThunk('testContext/get', async () => {
  return testContextService.get();
});

const setTestDate = createAsyncThunk('testContext/set', async (testDate: string) => {
  return testContextService.set(testDate);
});

const clearTestDate = createAsyncThunk('testContext/clear', async () => {
  await testContextService.clear();
});

const initialState: TestContextState = {
  testDate: null,
  loading: false,
  error: null,
};

export const testContextSlice = createSlice({
  name: 'testContext',
  initialState,
  reducers: {
    updateError(state, { payload: { error } }: PayloadAction<{ error: string | null }>) {
      state.error = error;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTestContext.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTestContext.fulfilled, (state, action) => {
        state.loading = false;
        state.testDate = action.payload.testDate;
      })
      .addCase(getTestContext.rejected, (state) => {
        // Silent failure — test context is non-critical; app falls back to real date
        state.loading = false;
      });

    builder
      .addCase(setTestDate.pending, (state) => {
        state.loading = true;
      })
      .addCase(setTestDate.fulfilled, (state, action) => {
        state.loading = false;
        state.testDate = action.payload.testDate;
        state.error = null;
      })
      .addCase(setTestDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Unable to set test date.';
      });

    builder
      .addCase(clearTestDate.pending, (state) => {
        state.loading = true;
      })
      .addCase(clearTestDate.fulfilled, (state) => {
        state.loading = false;
        state.testDate = null;
        state.error = null;
      })
      .addCase(clearTestDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Unable to clear test date.';
      });
  },
});

export const testContextActions = {
  ...testContextSlice.actions,
  getTestContext,
  setTestDate,
  clearTestDate,
};

export type TestContextSlice = {
  [testContextSlice.name]: ReturnType<typeof testContextSlice['reducer']>;
};
